import React, { memo } from 'react';
import { Tooltip } from '@mui/material';
import { getExpression } from '@shared/helpers/expression-solving.helpers';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { isProductCategoryElement } from '@shared/helpers/element-category.helpers';
import { selectableUnitHarmonizer } from '@shared/helpers/unit.helpers';
import { IProductElement } from '@shared/models/project.model';
import {
  CONTENT_CELL_WIDTH,
  CONTENT_CELL_WIDTH_RESPONSIVE,
  LIST_ITEM_HEIGHT,
  LIST_SPACING,
  ROOT_CELL_WIDTH,
  Row,
  RowCell,
  useListRowStyles,
} from '@ui/ElementList';
import { ExpandButton } from '@ui/ElementList/Buttons/ExpandButton';
import EPDMenu from '@features/epd/EPDMenu/EPDMenu';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import {
  useIsDeactivated,
  useParentElement,
} from '@client/hooks/use-elements.hooks';
import { useToggleElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { useTriggerContextMenu } from '@client/hooks/use-menu.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useIsSelected } from '@client/store/ui/ui.hook';
import ElementBarCharts from '@features/projects/charts/ElementBarCharts';
import {
  useGenericProduct,
  useSelectedVersionProduct,
} from './use-products.hooks';
import { EllipsisText } from '@ui/EllipsisText';
import { guards } from '@shared/helpers/typeguards.helpers';

type IProductElementListItemInput = {
  element: IProductElement;
  indentation?: number;
};

const ProductElementListItem: React.FC<IProductElementListItemInput> = ({
  element,
  indentation = 0,
}) => {
  const { classes: listClasses } = useListRowStyles();

  const { id, product_id } = element;

  const triggerContextMenu = useTriggerContextMenu(id);
  const toggleElementExpanded = useToggleElementExpanded(element);

  const selected = useIsSelected(element);
  const deactivated = useIsDeactivated(element);

  const parent = useParentElement(element);
  const product = useSelectedVersionProduct(product_id, false);
  const genericProduct = useGenericProduct(element);
  const productCategoryElement = isProductCategoryElement(parent)
    ? parent
    : undefined;

  const [hover, startHover, endHover] = useBooleanState(false);

  // When swapping products the product might get removed for one render (due to AnimatedList and some component caching?!)
  if (!product) {
    return null;
  }

  const name = [element.name, genericProduct?.name ?? product.name]
    .filter(guards.is.defined)
    .join(': ');

  const expressionValue = getExpression(element.count);

  const expressionResultWithUnit = `${expressionValue.formatted} ${selectableUnitHarmonizer(element.unit)}`;
  return (
    <Row
      classes={listClasses}
      deactivated={deactivated}
      height={LIST_ITEM_HEIGHT}
      hover={hover}
      onContextMenu={triggerContextMenu}
      onDoubleClick={toggleElementExpanded}
      onMouseLeave={endHover}
      onMouseOver={startHover}
      padding={true}
      selected={selected}
      spacing={LIST_SPACING.DEFAULT}
    >
      {/* CONTENT. Group content in a shared cell to make sure we can align bar charts as one */}
      <RowCell
        indentation={indentation}
        width={ROOT_CELL_WIDTH.CONTENT}
        paddingRight={LIST_SPACING}
      >
        <Row gap={0}>
          {/* Name */}
          <RowCell
            width={CONTENT_CELL_WIDTH.NAME}
            responsiveWidth={CONTENT_CELL_WIDTH_RESPONSIVE.NAME}
          >
            {/* this is just as a spacer */}
            <ExpandButton element={element} disabled={true} hidden />
            <ExpandButton element={element} disabled={true} />
            <EllipsisText
              verticalAlign="middle"
              tooltipTitle={name}
              color="neutral"
            >
              {name}
            </EllipsisText>
          </RowCell>

          {/* EPD selector */}
          <RowCell align="right" width={CONTENT_CELL_WIDTH.EXPRESSION}>
            {isElement(parent) && (hover || selected || genericProduct) && (
              <EPDMenu
                variant="button"
                product_id={product_id}
                generic_product_id={genericProduct?.id}
                productCategoryElement={productCategoryElement}
              />
            )}
          </RowCell>
        </Row>
      </RowCell>

      {/* Bar chart */}
      <RowCell>
        <ElementBarCharts element={element} />
      </RowCell>

      {/* Expression display */}
      <RowCell align="right" width={CONTENT_CELL_WIDTH.METRICS}>
        {/* ProductElements are always disabled so show a tooltip showing expression for ease of use */}
        <Tooltip
          title={`${expressionValue.expression} = ${expressionResultWithUnit}`}
        >
          <span>
            {/* For some reason Tooltip throws a fit without the wrapper span */}
            <ExpressionTypography unit={element.unit} value={expressionValue} />
          </span>
        </Tooltip>
      </RowCell>
    </Row>
  );
};

export default memo(ProductElementListItem);
